import React from 'react';
import { Box, useTheme } from '@mui/material';
import { Chip } from '../Chip';
import { TitleField } from '../TitleField';
import { Typography } from '../Typography';

export interface DetailsSubheaderProps {
  /**
   * The main title text
   */
  title: string;
  /**
   * Optional subtitle text displayed below the title
   */
  subtitle?: string;
  /**
   * Display a chip with a number next to the title
   */
  titleCount?: number;
  /**
   * Optional slot for buttons and controls on the right from the `md` breakpoint,
   * or below the title/subtitle on smaller viewports.
   */
  controls?: React.ReactNode;
  /**
   * Optional test ID for the component
   */
  testId?: string;
  /**
   * Optional editable title configuration
   * When provided, renders a TitleField instead of static text
   */
  editableTitle?: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  };
}

/**
 * A responsive subheader component for detail pages that displays a title and subtitle
 * on the left, with optional controls on the right. Below the `md` breakpoint, title and
 * controls stack. At `md` and above they share a row; long titles truncate so controls are not overlapped.
 */
export const DetailsSubheader: React.FC<DetailsSubheaderProps> = ({
  title,
  subtitle,
  controls,
  testId,
  titleCount,
  editableTitle,
}) => {
  const theme = useTheme();
  const hasTitleCount = titleCount !== undefined && titleCount !== null;

  return (
    <Box
      data-testid={testId ? `details-subheader-${testId}` : 'details-subheader'}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.custom.spacing?.lg || 2,
        width: '100%',
      }}
    >
      {/* Left side: Title and Subtitle */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.custom.spacing?.xs || 0.5,
          flex: {
            xs: '1 1 100%',
            md: '1 1 0',
          },
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box display="flex" alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
          {editableTitle ? (
            <TitleField
              value={editableTitle.value}
              onChange={editableTitle.onChange}
              onBlur={editableTitle.onBlur}
              size="small"
              fullWidth
            />
          ) : (
            <Typography
              component="h2"
              sx={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#181D27',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          )}
          {hasTitleCount && (
            <Box data-testid="title-count" sx={{ ml: 3 }}>
              <Chip label={titleCount} variant="filled" size="medium" />
            </Box>
          )}
        </Box>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              lineHeight: '20px',
              color: '#535862',
              ml: editableTitle ? 2 : 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Right side: Controls */}
      {controls && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.custom.spacing?.md || 1,
            flex: {
              xs: '1 1 100%',
              md: '0 0 auto',
            },
            width: { xs: '100%', md: 'auto' },
            minWidth: { md: 'min-content' },
            maxWidth: '100%',
            justifyContent: {
              xs: 'flex-start',
              md: 'flex-end',
            },
          }}
        >
          {controls}
        </Box>
      )}
    </Box>
  );
};
